import { useState } from 'react';
import { Button, Modal, NativeSelect } from '@immo/ui-kit';
import type { FleetCommandView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { CommandOutput, FollowedCommand, FollowedCommandFooter } from './followed-command';

type Props = {
  readonly machineName: string;
  /** The projects the machine's workers serve; none chosen reports every project's worktrees. */
  readonly projects: ReadonlyArray<string>;
  /** The issued command, as the page's poll last read it; `null` while the form is up. */
  readonly command: FleetCommandView | null;
  readonly busy: boolean;
  /** Why the backend refused the issue, translated. */
  readonly error: string | null;
  /** `undefined`: every project the host serves. */
  readonly onSubmit: (project: string | undefined) => void;
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

/**
 * `system-preview` (fleet-agent ADR 0018): the host's disk report — what
 * `system-prune` would free, deleting nothing. A project narrows the worktree
 * pile; Docker and the pnpm store are reported either way. Once issued, the
 * dialog follows the command until it settles with the tail of the report.
 */
export function SystemPreviewDialog({
  machineName,
  projects,
  command,
  busy,
  error,
  onSubmit,
  onAgain,
  onClose,
}: Props) {
  const t = useTranslations('pages.admin.fleet.system-preview');
  const tFleet = useTranslations('pages.admin.fleet');
  const [project, setProject] = useState('');

  if (command !== null) {
    const payload = command.payload?.kind === 'system' ? command.payload : null;

    return (
      <Modal
        open
        onClose={onClose}
        confirmOnClose={false}
        size="lg"
        title={t('title')}
        footer={
          <FollowedCommandFooter command={command} againLabel={t('again-cta')} onAgain={onAgain} onClose={onClose} />
        }
      >
        <FollowedCommand
          scope={t('scope-line', { machine: machineName, project: command.project ?? t('every-project') })}
          command={command}
        >
          {payload !== null && <CommandOutput output={payload.output} truncated={payload.truncated} />}
        </FollowedCommand>
      </Modal>
    );
  }

  const submit = () => onSubmit(project === '' ? undefined : project);

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {tFleet('cancel-cta')}
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {t('cta')}
          </Button>
        </>
      }
    >
      <p className="font-mono text-base-sm text-text-secondary">{machineName}</p>
      <p className="mt-3 text-sm text-text-secondary">{t('body')}</p>

      {projects.length > 1 && (
        <div className="mt-4">
          <NativeSelect
            value={project}
            onChange={setProject}
            disabled={busy}
            aria-label={t('project-field')}
            options={[
              { value: '', label: t('every-project') },
              ...projects.map((name) => ({ value: name, label: name })),
            ]}
          />
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
    </Modal>
  );
}
